import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PillarData } from '../../lib/pillarTypes';
import KpiNavigation from '../../Components/KpiNavigation';
import ResourcesWorkspace from '../../Components/ResourcesWorkspace/ResourcesWorkspace';
import type { PreflightItem } from '../../Data/PreFlight/preflightTypes';
import { preflightStore, storeKey, preflightLocalWriteTs } from '../../Data/store';
import { upsertPreflightItem, deletePreflightItem, batchUpsertPreflightItems } from '../../api/preflightItems';
import { usePreflightLoader } from '../../hooks/usePreflightLoader';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import BudgetTab from '../../Components/BudgetTab/BudgetTab';
import { updateProject } from '../../api/projects';
import { projectConfigStore } from '../../Data/store';
import GanttPanel from '../../Components/GanttPanel/GanttPanel';
import ProjectTeamPanel from '../../Components/ProjectTeam/ProjectTeamPanel';
import { ContributionsPanel } from '../../Components/ContributionsPanel/ContributionsPanel';
import RisksIssuesTab from '../../Components/RisksIssuesTab/RisksIssuesTab';
import { fetchIssueFlags, type IssueFlagResponse } from '../../api/issueFlags';
import type { WorkspaceCardBadge } from '../../Components/WorkspaceCards/WorkspaceCards';
import WorkspaceCards from '../../Components/WorkspaceCards/WorkspaceCards';
import { buildWorkspaceCardItems } from '../../Components/WorkspaceCards/workspaceCardItems';
import styles from './ProjectDetail.module.css';

/* ------------------------------------------------------------------ */
/*  Sub-tab keys                                                       */
/* ------------------------------------------------------------------ */

export const WORK_SUB_TABS = ['performance', 'resources', 'budget', 'team', 'contributions', 'risksIssues'] as const;
export type WorkSubTabKey = (typeof WORK_SUB_TABS)[number];

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface WorkTabPanelProps {
  data: PillarData;
  /** Raw project record passed through to KpiNavigation. */
  projectPayload: any;
  currentUserGuid: string;
  /** When false, the Management tab is read-only (team members without PM/Owner/Admin). */
  canEdit?: boolean;
  debugMode?: boolean;
  onProjectConfigChanged?: () => void;
  /** Controlled KPI sub-tab index (0=Summary, 1=Schedule, 2=Finance, 3=Mission, 4=Data). */
  kpiTabIndex?: number;
  /** Fires when the user clicks a KPI tab inside the Performance view. */
  onKpiTabChange?: (index: number) => void;
  /** Controlled sub-tab key — parent renders the sub-tab bar externally. */
  activeSubTab?: WorkSubTabKey;
  /** Called when the sub-tab changes (e.g. KPI request forces Performance). */
  onSubTabChange?: (key: WorkSubTabKey) => void;
  /** Whether the Gantt editor portal is open. */
  ganttOpen?: boolean;
  /** Called to open the Gantt editor (controlled mode). */
  onOpenGantt?: () => void;
  /** Called to close the Gantt editor. */
  onCloseGantt?: () => void;
  /** When true, the component does NOT render its own sub-tab bar (parent renders it). */
  hideSubTabBar?: boolean;
  /** DEV-649: When true, the Live/Official toggle is hidden and data is locked to Official.
   *  Used by portfolio view to prevent portfolio managers from seeing draft KPI data. */
  forceOfficialOnly?: boolean;
  /** When provided, the Team sub-tab's ProjectTeamPanel starts in this view mode
   *  (e.g. 'digest' when arriving from the Engagement card's "View Digest" button). */
  initialTeamView?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function WorkTabPanel({
  data,
  projectPayload,
  currentUserGuid,
  canEdit = true,
  debugMode,
  onProjectConfigChanged,
  kpiTabIndex = 0,
  onKpiTabChange,
  activeSubTab: controlledSubTab,
  onSubTabChange,
  ganttOpen: controlledGanttOpen,
  onOpenGantt: controlledOpenGantt,
  onCloseGantt: controlledCloseGantt,
  hideSubTabBar = false,
  forceOfficialOnly = false,
  initialTeamView,
}: WorkTabPanelProps) {
  const { t } = useAppTranslation('kpi');
  const { t: tPages } = useAppTranslation('pages');

  /* ── Internal state (used when parent does NOT control it) ── */
  const [internalSubTab, setInternalSubTab] = useState<WorkSubTabKey>('performance');
  const [internalGanttOpen, setInternalGanttOpen] = useState(false);
  const preGanttTabRef = useRef<WorkSubTabKey>('performance');

  /* Resolve controlled vs internal */
  const activeSubTab = controlledSubTab ?? internalSubTab;
  const setActiveSubTab = onSubTabChange ?? setInternalSubTab;
  const ganttOpen = controlledGanttOpen ?? internalGanttOpen;

  const handleOpenGantt = useCallback(() => {
    preGanttTabRef.current = activeSubTab;
    if (controlledOpenGantt) {
      controlledOpenGantt();
    } else {
      setInternalGanttOpen(true);
    }
  }, [activeSubTab, controlledOpenGantt]);

  const handleCloseGantt = useCallback(() => {
    if (controlledCloseGantt) {
      controlledCloseGantt();
    } else {
      setInternalGanttOpen(false);
      setInternalSubTab(preGanttTabRef.current);
    }
  }, [controlledCloseGantt]);

  /* ── KPI tab state ── */
  const [localKpiTab, setLocalKpiTab] = useState(kpiTabIndex);

  const projectGuid = projectPayload?.project_GUID || projectPayload?.project_guid || '';
  const projectName = projectPayload?.project_name || data?.name || 'Project';

  /* Pre-flight data */
  const { preflightVersion, bumpVersion: bumpPreflightVersion } = usePreflightLoader(
    projectGuid,
    true
  );

  /* ── Open-flag summary for the Risks & Issues card badge ──────
   * Parent-level fetch so the badge is live regardless of which
   * sub-tab is active. RisksIssuesTab notifies us via onFlagsChanged
   * after any resolve/dismiss/reopen so we stay in sync without
   * SignalR (see DEV-<follow-up> for the real-time upgrade).
   */
  const [openFlagCount, setOpenFlagCount] = useState(0);
  const [maxOpenSeverity, setMaxOpenSeverity] =
    useState<'blocking' | 'warning' | 'informational' | null>(null);

  const summarizeOpenFlags = useCallback((flags: IssueFlagResponse[]) => {
    const open = flags.filter((f) => f.status === 'open');
    setOpenFlagCount(open.length);
    if (open.length === 0) {
      setMaxOpenSeverity(null);
      return;
    }
    // Rank: blocking > warning > informational
    if (open.some((f) => f.severity === 'blocking'))      setMaxOpenSeverity('blocking');
    else if (open.some((f) => f.severity === 'warning'))  setMaxOpenSeverity('warning');
    else                                                   setMaxOpenSeverity('informational');
  }, []);

  const refreshFlagSummary = useCallback(async () => {
    if (!projectGuid) {
      setOpenFlagCount(0);
      setMaxOpenSeverity(null);
      return;
    }
    try {
      const flags = await fetchIssueFlags(projectGuid);
      summarizeOpenFlags(flags);
    } catch (err) {
      // Don't surface an error UI for a background badge fetch — log and carry on.
      console.warn('[WorkTabPanel] flag summary refresh failed:', err);
    }
  }, [projectGuid, summarizeOpenFlags]);

  useEffect(() => {
    refreshFlagSummary();
  }, [refreshFlagSummary]);

  /* Build the badge descriptor once per count/severity change. */
  const risksIssuesBadge: WorkspaceCardBadge | undefined = useMemo(() => {
    if (openFlagCount <= 0) return undefined;
    const tone =
      maxOpenSeverity === 'blocking' ? 'critical' :
      maxOpenSeverity === 'warning'  ? 'warning'  :
                                       'info';
    return {
      count: openFlagCount,
      tone,
      ariaLabel: `${openFlagCount} open flag${openFlagCount === 1 ? '' : 's'}${
        maxOpenSeverity === 'blocking' ? ', at least one blocking' :
        maxOpenSeverity === 'warning'  ? ''                         :
                                         ''
      }`,
    };
  }, [openFlagCount, maxOpenSeverity]);

  /* Sync when parent changes the KPI tab prop */
  const prevPropRef = useRef(kpiTabIndex);
  useEffect(() => {
    if (kpiTabIndex !== prevPropRef.current) {
      setLocalKpiTab(kpiTabIndex);
      setActiveSubTab('performance');
    }
    prevPropRef.current = kpiTabIndex;
  }, [kpiTabIndex, setActiveSubTab]);

  const handleKpiTabChange = useCallback(
    (idx: number) => {
      setLocalKpiTab(idx);
      onKpiTabChange?.(idx);
    },
    [onKpiTabChange]
  );

  /* ── Contingency % save handler ── */
  const handleContingencyChange = useCallback(
    async (pct: number) => {
      if (!projectGuid) return;
      await updateProject(projectGuid, { contingencyPercent: pct });
      // Also update the in-memory config store so that the "local wins" merge
      // in hydrateProjectConfigs doesn't overwrite the fresh API value with
      // the stale store value during the same session.
      const guidUp = projectGuid.toUpperCase();
      const existing = projectConfigStore.get(guidUp) || {};
      projectConfigStore.set(guidUp, { ...existing, contingency_percent: pct });
      onProjectConfigChanged?.();
    },
    [projectGuid, onProjectConfigChanged]
  );

  /* ── Pre-flight items from store ── */
  const preflightItems: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    const key = storeKey(projectGuid);
    return preflightStore.get(key) || [];
  }, [projectGuid, preflightVersion]);

  /** Optimistic update: write to store immediately, persist to API in background. */
  const handlePreflightItemsChange = useCallback(
    (updatedItems: PreflightItem[]) => {
      if (!projectGuid) return;
      const key = storeKey(projectGuid);
      const previousItems = preflightStore.get(key) || [];

      preflightStore.set(key, updatedItems);
      preflightLocalWriteTs.set(key, Date.now());
      bumpPreflightVersion();

      try { window.dispatchEvent(new CustomEvent('l4e_preflight_changed')); } catch { /* ignore */ }

      const prevGuids = new Set(previousItems.map((i) => i.item_guid.toUpperCase()));
      const newGuids = new Set(updatedItems.map((i) => i.item_guid.toUpperCase()));
      const deletedGuids = Array.from(prevGuids).filter((g) => !newGuids.has(g));

      const upsertItems = updatedItems.filter((item) => {
        const prev = previousItems.find(
          (p) => p.item_guid.toUpperCase() === item.item_guid.toUpperCase()
        );
        if (!prev) return true;
        return (
          prev.label !== item.label ||
          prev.phase !== item.phase ||
          prev.category !== item.category ||
          prev.description !== item.description ||
          prev.requirement_type !== item.requirement_type ||
          prev.target_quantity !== item.target_quantity ||
          prev.unit !== item.unit ||
          prev.current_quantity !== item.current_quantity ||
          prev.is_fulfilled !== item.is_fulfilled ||
          prev.state !== item.state ||
          prev.priority !== item.priority ||
          prev.visibility !== item.visibility ||
          prev.mission_subkpi_key !== item.mission_subkpi_key ||
          prev.sort_order !== item.sort_order ||
          prev.cost_category !== item.cost_category ||
          prev.unit_cost !== item.unit_cost ||
          prev.actual_cost !== item.actual_cost ||
          prev.cost_currency !== item.cost_currency ||
          prev.cost_note !== item.cost_note ||
          prev.gantt_task_id !== item.gantt_task_id ||
          prev.percent_complete !== item.percent_complete ||
          prev.fulfillment_note !== item.fulfillment_note ||
          prev.assignee_guid !== item.assignee_guid ||
          JSON.stringify(prev.tags) !== JSON.stringify(item.tags) ||
          JSON.stringify(prev.depends_on) !== JSON.stringify(item.depends_on) ||
          JSON.stringify(prev.descriptor_tags) !== JSON.stringify(item.descriptor_tags)
        );
      });

      if (upsertItems.length > 0) {
        console.info(`[PreFlight] Persisting ${upsertItems.length} item(s) — ${upsertItems.length > 3 ? 'batch' : 'individual'} mode`);
      }
      if (upsertItems.length > 3) {
        batchUpsertPreflightItems(upsertItems).catch((batchErr) => {
          console.warn('[PreFlight] Batch upsert failed:', batchErr.message, '— falling back to individual upserts');
          let failCount = 0;
          const promises = upsertItems.map((item) =>
            upsertPreflightItem(item).catch((itemErr) => {
              failCount++;
              console.error(`[PreFlight] Individual fallback upsert failed for ${item.item_guid}:`, itemErr.message);
            })
          );
          Promise.allSettled(promises).then(() => {
            if (failCount > 0) {
              console.error(`[PreFlight] ${failCount}/${upsertItems.length} items failed to persist — data may be lost on refresh`);
              try {
                window.dispatchEvent(new CustomEvent('l4e_preflight_save_error', {
                  detail: { failCount, totalCount: upsertItems.length },
                }));
              } catch { /* ignore */ }
            } else {
              console.info(`[PreFlight] All ${upsertItems.length} items persisted via individual fallback`);
            }
          });
        });
      } else {
        for (const item of upsertItems) {
          upsertPreflightItem(item).catch((err) => {
            console.error(`[PreFlight] Upsert failed for ${item.item_guid}:`, err.message);
          });
        }
      }

      for (const guid of deletedGuids) {
        const original = previousItems.find((p) => p.item_guid.toUpperCase() === guid);
        if (original) {
          deletePreflightItem(original.item_guid).catch((err) => {
            console.warn(`[PreFlight] Delete failed for ${original.item_guid}:`, err.message);
          });
        }
      }
    },
    [projectGuid, bumpPreflightVersion]
  );

  /* ── Render ── */
  return (
    <div className={styles.panel}>
      {/* Sub-tab bar — color-coded workspace cards (I-1 design) */}
      {!hideSubTabBar && (
        <WorkspaceCards
          items={buildWorkspaceCardItems(
            {
              performance: tPages('projectDetail.management.performance'),
              resources: t('preflight.title'),
              budget: tPages('budget.tabTitle'),
              team: tPages('projectDetail.management.team'),
              contributions: tPages('projectDetail.management.contributions'),
              risksIssues: tPages('projectDetail.management.risksIssues'),
              gantt: tPages('gantt.title'),
            },
            { ganttDisabled: !projectGuid, risksIssuesBadge },
          )}
          activeKey={activeSubTab}
          onSelect={(key) => {
            if (key === 'gantt') {
              handleOpenGantt();
            } else {
              setActiveSubTab(key as WorkSubTabKey);
            }
          }}
        />
      )}

      {/* Performance sub-tab */}
      {activeSubTab === 'performance' && (
        <>
          {projectPayload ? (
            <KpiNavigation
              payload={projectPayload}
              currentUserGuid={currentUserGuid}
              selectedTabIndex={localKpiTab}
              onTabIndexChange={handleKpiTabChange}
              debugMode={debugMode}
              onProjectConfigChanged={onProjectConfigChanged}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                Select a project to view KPI performance data.
              </p>
            </div>
          )}
        </>
      )}

      {/* Resources sub-tab */}
      {activeSubTab === 'resources' && (
        <>
          {projectGuid ? (
            <ResourcesWorkspace
              projectGuid={projectGuid}
              projectName={projectName}
              budgetCurrency={projectPayload?.budget_currency || 'USD'}
              items={preflightItems}
              onItemsChange={handlePreflightItemsChange}
              canEdit={canEdit}
              debugMode={debugMode}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                Select a project to view its resources.
              </p>
            </div>
          )}
        </>
      )}

      {/* Budget sub-tab */}
      {activeSubTab === 'budget' && (
        <>
          {projectGuid ? (
            <BudgetTab
              projectGuid={projectGuid}
              projectName={projectName}
              budgetCurrency={projectPayload?.budget_currency || 'USD'}
              contingencyPercent={projectPayload?.contingency_percent ?? 10}
              onContingencyChange={handleContingencyChange}
              canEdit={canEdit}
              debugMode={debugMode}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                {tPages('budget.tabTitle')}
              </p>
            </div>
          )}
        </>
      )}

      {/* Team sub-tab */}
      {activeSubTab === 'team' && (
        <>
          {projectGuid ? (
            <ProjectTeamPanel
              projectGuid={projectGuid}
              projectName={projectName}
              ownerGuid={projectPayload?.created_by || currentUserGuid}
              canEdit={canEdit}
              debugMode={debugMode}
              initialViewMode={initialTeamView}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                {tPages('projectDetail.management.selectProjectTeam')}
              </p>
            </div>
          )}
        </>
      )}

      {/* Contributions sub-tab */}
      {activeSubTab === 'contributions' && (
        <>
          {projectGuid ? (
            <ContributionsPanel
              projectGuid={projectGuid}
              projectName={projectName}
              canEdit={canEdit}
              debugMode={debugMode}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                {tPages('projectDetail.management.selectProjectContributions')}
              </p>
            </div>
          )}
        </>
      )}

      {/* Risks & Issues sub-tab */}
      {activeSubTab === 'risksIssues' && (
        <>
          {projectGuid ? (
            <RisksIssuesTab
              projectGuid={projectGuid}
              canEdit={canEdit}
              debugMode={debugMode}
              onFlagsChanged={refreshFlagSummary}
            />
          ) : (
            <div className={styles.placeholder}>
              <p className={styles.placeholderText}>
                {tPages('projectDetail.risksIssues.selectProject')}
              </p>
            </div>
          )}
        </>
      )}

      {/* Gantt — portaled full-screen editor */}
      {ganttOpen && projectGuid && (
        <GanttPanel
          projectGuid={projectGuid}
          projectName={projectName}
          canEdit={canEdit}
          debugMode={debugMode}
          autoOpen
          onClose={handleCloseGantt}
        />
      )}
    </div>
  );
}
